<template>
  <modal :title="$t('Payment Information')" :displayed="displayed" size="fullscreen" @close="$emit('close')">
    <button slot="title-action" v-t type="button" form="paymentInfoForm" @click.prevent="submitPaymentInfo">Save</button>
    <vue-form id="paymentInfoForm" slot="body" :state="paymentInfoState" @submit.prevent="submitPaymentInfo">
      <img class="cheque" src="~@imagesBrand/void-cheque.svg" :alt="$t('Void Cheque')">
      <section>
        <validate class="field">
          <label for="branch">
            <span class="numbering colour-1">1</span>
            <span v-t>Branch Number</span>
          </label>
          <input
            id="branch"
            v-model.trim="paymentInfoBranch"
            type="text"
            name="branch"
            :class="{ invalid: inputError.branch.$invalid }"
            minlength="5"
            maxlength="5"
            isplainnumber
            @blur="updateError('branch'); updateBankInfo();"
          >
          <div v-if="inputError.branch.$bankRequired || inputError.branch.$error" class="error-message">
            <div v-if="branchNumberError" v-t>Must be exactly 5 digits including zeros</div>
          </div>
        </validate>
        <validate class="field">
          <label for="institution">
            <span class="numbering colour-2">2</span>
            <span v-t>Institution Number</span>
          </label>
          <input
            id="institution"
            v-model.trim="paymentInfoInstitution"
            type="text"
            name="institution"
            :class="{ invalid: inputError.institution.$invalid }"
            minlength="3"
            maxlength="3"
            isplainnumber
            @blur="updateError('institution'); updateBankInfo();"
          >
          <div v-if="inputError.institution.$bankRequired || inputError.institution.$error" class="error-message">
            <div v-if="institutionNumberError" v-t>Must be exactly 3 digits including zeroes</div>
          </div>
        </validate>
        <validate class="field">
          <label for="account">
            <span class="numbering colour-3">3</span>
            <span v-t>Account Number</span>
          </label>
          <input
            id="account"
            v-model.trim="paymentInfoAccount"
            type="text"
            name="account"
            :class="{ invalid: inputError.account.$invalid }"
            minlength="5"
            maxlength="12"
            isplainnumber
            @blur="updateError('account');"
          >
          <div v-if="inputError.account.$bankRequired || inputError.account.$error" class="error-message">
            <div v-if="accountNumberError" v-t>5 to 12 digits without spaces or hyphens</div>
          </div>
        </validate>
      </section>
      <section v-if="bankInfo" class="bank-info">
        <span v-if="bankInfo.isUnknownBank" v-t>We're not familiar with this bank.</span>
        <span v-if="bankInfo.institution" v-t>Your bank is:</span>
        &nbsp; {{ bankInfo.institution }}
        <em v-if="bankInfo.address" class="bank-address">{{ bankInfo.address }}</em>
        <button v-t type="button" class="silver" @click="removeBankAccount()">Remove</button>
      </section>
    </vue-form>
  </modal>
</template>

<script>
import { modal } from '@/components/layout';
import { mapGetters, mapActions } from '@/lib/vue';
import errorStore from '@/store/error';

const _ = {
  errorStore,
};

export default {
  _,
  name: 'PaymentInfo',
  components: { modal },
  props: {
    displayed: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      paymentInfoState: {},
      inputError: {
        branch: {},
        institution: {},
        account: {},
      },
      paymentInfoBranch: '',
      paymentInfoInstitution: '',
      paymentInfoAccount: '',
      bankInfo: null,
    };
  },
  computed: {
    ...mapGetters(['paymentInfo']),
    branchNumberError() {
      return (
        this.inputError.branch.$bankRequired
        || this.inputError.branch.$error.isplainnumber
        || this.inputError.branch.$error.minlength
        || this.inputError.branch.$error.maxlength
      );
    },
    institutionNumberError() {
      return (
        this.inputError.institution.$bankRequired
        || this.inputError.institution.$error.isplainnumber
        || this.inputError.institution.$error.minlength
        || this.inputError.institution.$error.maxlength
      );
    },
    accountNumberError() {
      return (
        this.inputError.account.$bankRequired
        || this.inputError.account.$error.isplainnumber
        || this.inputError.account.$error.minlength
        || this.inputError.account.$error.maxlength
      );
    },
  },
  watch: {
    async displayed(value) {
      if (value === true) {
        try {
          await this.fetchPaymentInfoNoLoader();
          this.paymentInfoBranch = this.paymentInfo.transitNumber;
          this.paymentInfoInstitution = this.paymentInfo.institutionNumber;
          this.paymentInfoAccount = this.paymentInfo.accountNumber;
          this.inputError = {
            branch: {},
            institution: {},
            account: {},
          };
        } catch (exception) {
          _.errorStore.addError();
        }
      }
    },
  },
  async created() {
    try {
      await this.fetchPaymentInfoNoLoader();
      this.paymentInfoBranch = this.paymentInfo.transitNumber;
      this.paymentInfoInstitution = this.paymentInfo.institutionNumber;
      this.paymentInfoAccount = this.paymentInfo.accountNumber;
    } catch (exception) {
      _.errorStore.addError();
    }
  },
  methods: {
    ...mapActions([
      'fetchPaymentInfoNoLoader',
      'updatePaymentInfo',
      'fetchBankInfo',
    ]),

    updateError(fieldName) {
      this.inputError[fieldName] = Object.assign(
        {},
        this.paymentInfoState[fieldName],
      );
      this.inputError[fieldName].$error = Object.assign(
        {},
        this.paymentInfoState[fieldName].$error,
      );
    },

    updateAllErrors() {
      this.updateError('branch');
      this.updateError('institution');
      this.updateError('account');
      this.validateBankSection();
    },

    isBankSectionValid() {
      let numberOfBankFieldsFilled = 0;
      if (this.paymentInfoBranch !== '') {
        ++numberOfBankFieldsFilled;
      }
      if (this.paymentInfoInstitution !== '') {
        ++numberOfBankFieldsFilled;
      }
      if (this.paymentInfoAccount !== '') {
        ++numberOfBankFieldsFilled;
      }
      if (numberOfBankFieldsFilled === 0 || numberOfBankFieldsFilled === 3) {
        return true;
      }
      return false;
    },

    displayBankSectionErrors(isValid) {
      this.inputError.branch.$bankRequired = isValid
        ? undefined
        : this.paymentInfoBranch === '';
      this.inputError.institution.$bankRequired = isValid
        ? undefined
        : this.paymentInfoInstitution === '';
      this.inputError.account.$bankRequired = isValid
        ? undefined
        : this.paymentInfoAccount === '';
    },

    validateBankSection() {
      this.displayBankSectionErrors(this.isBankSectionValid());
    },

    async updateBankInfo() {
      if (
        this.paymentInfoInstitution !== ''
        && !this.inputError.institution.$invalid
        && this.paymentInfoBranch !== ''
        && !this.inputError.branch.$invalid
      ) {
        try {
          this.bankInfo = await this.fetchBankInfo({
            institutionNumber: this.paymentInfoInstitution,
            transitNumber: this.paymentInfoBranch,
          });
        } catch (exception) {
          // Micr Jagger provides details about the user bank accounts.
          // The user does not need to see its errors it would be confusing.
        }
      } else {
        this.bankInfo = null;
      }
    },

    removeBankAccount() {
      this.paymentInfoBranch = '';
      this.paymentInfoInstitution = '';
      this.paymentInfoAccount = '';
      this.bankInfo = null;
      this.validateBankSection();
    },

    async submitPaymentInfo() {
      if (this.paymentInfoState.$invalid || !this.isBankSectionValid()) {
        this.updateAllErrors();
      } else {
        const paymentInfo = Object.assign({}, this.paymentInfo, {
          transitNumber: this.paymentInfoBranch,
          institutionNumber: this.paymentInfoInstitution,
          accountNumber: this.paymentInfoAccount,
        });
        try {
          const result = await this.updatePaymentInfo(paymentInfo);
          if (result.success === true) {
            this.$emit('close');
          } else {
            _.errorStore.addError();
          }
        } catch (exception) {
          _.errorStore.addError();
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
#paymentInfoForm {
  max-width: 28em;
  margin: auto;

  > img {
    margin: 0 0.5rem;
    max-width: 100%;

    @media #{$screen-width-medium} {
      margin: 0 1rem;
    }
  }

  .bank-info {
    flex-direction: column;
    color: $black;
    padding-bottom: 0.5rem;

    > * {
      display: block;
    }

    .bank-address {
      margin: 0.5rem 0 0.75rem;
    }

    button {
      margin-top: 0.25rem;
    }

    @media #{$screen-width-medium} {
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
}
.numbering {
  line-height: 1.25rem;
  font-weight: bold;
  background: $neutral-300;
  color: $white;
  border-radius: 50%;
  margin-right: 0.25rem;
  display: inline-block;
  text-align: center;
  width: 1.25rem;
  height: 1.25rem;
}

.cheque {
  margin-bottom: 1rem;
}

.colour-1 {
  background: $primary-300;
}

.colour-2 {
  background: $secondary-300;
}

.colour-3 {
  background: $tertiary-100;
  color: $black;
}
</style>
